import { cn } from '@/lib/utils';
import { FileSignature, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useMemo } from 'react';

interface TemplateVariablesStepProps {
  variables: string[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  extractedMetadata?: Record<string, string>;
}

// Humanize variable names: company_name → Company Name
function humanizeLabel(variable: string): string {
  return variable
    .replace(/[_-]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// Find matching metadata key (case-insensitive, underscore-insensitive)
function findMatchingMetadata(
  variable: string,
  metadata?: Record<string, string>
): string | undefined {
  if (!metadata) return undefined;

  const normalizedVar = variable.toLowerCase().replace(/[_-]/g, '');

  for (const [key, value] of Object.entries(metadata)) {
    const normalizedKey = key.toLowerCase().replace(/[_-]/g, '');
    if (normalizedKey === normalizedVar && value) {
      return value;
    }
  }

  return undefined;
}

const ITEMS_PER_PAGE = 3;

export function TemplateVariablesStep({
  variables,
  values,
  onChange,
  extractedMetadata,
}: TemplateVariablesStepProps) {
  const [currentPage, setCurrentPage] = useState(0);

  // Pre-fill values from extracted metadata on initial render
  const prefilledValues = useMemo(() => {
    const result: Record<string, string> = { ...values };
    for (const variable of variables) {
      if (!result[variable]) {
        const match = findMatchingMetadata(variable, extractedMetadata);
        if (match) {
          result[variable] = match;
        }
      }
    }
    return result;
  }, [variables, extractedMetadata, values]);

  const totalPages = Math.ceil(variables.length / ITEMS_PER_PAGE);
  const startIndex = currentPage * ITEMS_PER_PAGE;
  const currentVariables = variables.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );

  const handlePrevious = () => {
    setCurrentPage((p) => Math.max(0, p - 1));
  };

  const handleNext = () => {
    setCurrentPage((p) => Math.min(totalPages - 1, p + 1));
  };

  if (variables.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-accent-500/20">
          <FileSignature className="h-6 w-6 text-accent-400" />
        </div>
        <h3 className="text-lg font-semibold text-white">
          Help us prepare your proposal
        </h3>
        <p className="mt-1 text-sm text-slate-400">
          These details help us personalize your proposal. All fields are
          optional.
        </p>
      </div>

      <div className="space-y-4">
        {currentVariables.map((variable) => {
          const label = humanizeLabel(variable);
          const prefilled = findMatchingMetadata(variable, extractedMetadata);
          const value = values[variable] ?? prefilledValues[variable] ?? '';

          return (
            <div key={variable} className="space-y-1.5">
              <label
                htmlFor={`var-${variable}`}
                className="block text-sm font-medium text-slate-300"
              >
                {label}
                {prefilled && (
                  <span className="ml-2 text-xs font-normal text-slate-500">
                    (auto-filled from your documents)
                  </span>
                )}
              </label>
              <input
                id={`var-${variable}`}
                type="text"
                value={value}
                onChange={(e) => onChange(variable, e.target.value)}
                placeholder={`Enter ${label.toLowerCase()}`}
                className={cn(
                  'w-full rounded-lg border bg-slate-800 px-4 py-3 text-sm text-white',
                  'placeholder:text-slate-500',
                  'focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20',
                  'border-slate-700'
                )}
              />
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            onClick={handlePrevious}
            disabled={currentPage === 0}
            className={cn(
              'flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              currentPage === 0
                ? 'cursor-not-allowed text-slate-600'
                : 'text-slate-400 hover:bg-slate-800 hover:text-white'
            )}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </button>

          <span className="text-sm text-slate-500">
            {currentPage + 1} of {totalPages}
          </span>

          <button
            type="button"
            onClick={handleNext}
            disabled={currentPage === totalPages - 1}
            className={cn(
              'flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              currentPage === totalPages - 1
                ? 'cursor-not-allowed text-slate-600'
                : 'text-slate-400 hover:bg-slate-800 hover:text-white'
            )}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      <p className="text-center text-xs text-slate-500">
        You can skip this step if you prefer to provide these details later
      </p>
    </div>
  );
}
